"use client";

import { motion } from "motion/react";
import type { ReactNode } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { useIntroDone } from "@/components/motion/stage";
import { PageTitleLines } from "@/components/shared/page-title";
import { cn } from "@/lib/utils";

// =============================================================================
// Page heading animations played once the intro preloader has lifted (on a
// first visit they would otherwise play under the curtain). The animation
// state follows the intro store: the server markup hydrates untouched.
// =============================================================================

type IntroPageTitleProps = {
  /** One string per line: ["ALL MY", "CREATIONS"] */
  lines: string[];
  className?: string;
  delay?: number;
  /** Coral square dot after the last line */
  withDot?: boolean;
};

/**
 * Same look and motion as PageTitle (lines rising from a mask, then the coral
 * dot), triggered by the end of the intro.
 */
export function IntroPageTitle({
  lines,
  className,
  delay = 0.15,
  withDot = true,
}: IntroPageTitleProps) {
  const introDone = useIntroDone();

  return (
    <motion.h1
      initial="hidden"
      animate={introDone ? "visible" : "hidden"}
      transition={{ staggerChildren: 0.09, delayChildren: delay }}
      className={cn("text-display", className)}
      aria-label={lines.join(" ")}
    >
      <PageTitleLines lines={lines} withDot={withDot} />
    </motion.h1>
  );
}

// =============================================================================

type IntroRevealProps = {
  children: ReactNode;
  delay?: number;
  /** Vertical offset in px */
  y?: number;
  className?: string;
};

/**
 * Fades and slides its content in once the intro has lifted.
 */
export function IntroReveal({
  children,
  delay = 0,
  y = 16,
  className,
}: IntroRevealProps) {
  const introDone = useIntroDone();

  return (
    <motion.div
      initial={{ opacity: 0, y }}
      animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, delay, ease: EASE_OUT_EXPO }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// =============================================================================
